import * as Sentry from '@sentry/react';
import { useEffect, useRef } from 'react';
import type {
  AutoCompleteShipInfo,
  FindTargetResult,
} from '@ais-ui/websockets';

/**
 * Sentry observability for the remote-control ("external ship selector") zoom
 * flow: external system POSTs /ship/select → we poll it → findTarget → zoom →
 * select. Every failure after the poll used to be a toast or console.error only.
 */

/** Failures before a ship is known (the /ship/select poll itself) */
export type RemoteSelectionFailureReason =
  | 'poll-error'
  | 'empty-ship-list'
  | 'no-ship-identifier'
  | 'slow-poll';

/** Failures while zooming to / selecting a remotely requested ship */
export type RemoteZoomFailureReason =
  | 'no-target'
  | 'no-position'
  | 'find-target-error'
  | 'no-map-dimensions'
  | 'overlay-error'
  | 'target-info-error'
  | 'no-target-info'
  | 'invalid-mmsi';

type ZoomShip = Pick<AutoCompleteShipInfo, 'mmsi' | 'imo' | 'source'>;

const isRemote = (ship: ZoomShip | null | undefined): ship is ZoomShip =>
  ship?.source === 'remote';

const errorMessage = (error: unknown): string | undefined => {
  if (error === undefined || error === null) {
    return undefined;
  }
  return error instanceof Error ? error.message : String(error);
};

export const addRemoteZoomBreadcrumb = (
  message: string,
  data?: Record<string, unknown>,
) => {
  Sentry.addBreadcrumb({
    category: 'remote-zoom',
    message,
    level: 'info',
    data,
  });
};

const captureFailure = (
  reason: RemoteSelectionFailureReason | RemoteZoomFailureReason,
  ship: ZoomShip | null,
  error?: unknown,
  details?: Record<string, unknown>,
) => {
  Sentry.captureMessage(`Remote zoom failed: ${reason}`, {
    level: 'warning',
    fingerprint: ['remote-zoom-failed', reason],
    tags: {
      component: 'RemoteZoom',
      'remote_zoom.reason': reason,
      'remote_zoom.mmsi': ship?.mmsi ?? undefined,
    },
    extra: {
      mmsi: ship?.mmsi,
      imo: ship?.imo,
      error: errorMessage(error),
      ...details,
    },
  });
};

export const reportRemoteSelectionFailure = (
  reason: RemoteSelectionFailureReason,
  error?: unknown,
  details?: Record<string, unknown>,
) => {
  captureFailure(reason, null, error, details);
};

/** Normal /ship/select polls answer well under a second (poll interval is 5 s). */
export const SLOW_REMOTE_POLL_MS = 15_000;

/**
 * Reports a /ship/select poll that is still pending after SLOW_REMOTE_POLL_MS.
 * React Query doesn't start a new poll while one is in flight, so a hanging
 * request silently stops remote zoom. Reported while pending so it's captured
 * even if the request never completes (e.g. the tab is closed).
 */
export const useReportSlowRemotePoll = (isFetching: boolean) => {
  // Set once a poll has been reported slow; cleared when that poll completes
  const slowPollStartedAt = useRef<number | null>(null);

  useEffect(() => {
    if (!isFetching) {
      // Only reached on completion, not on unmount, so an unmounted pending
      // poll isn't misreported as completed
      if (slowPollStartedAt.current !== null) {
        addRemoteZoomBreadcrumb('slow poll completed', {
          durationMs: Date.now() - slowPollStartedAt.current,
        });
        slowPollStartedAt.current = null;
      }
      return;
    }

    const startedAt = Date.now();
    const timer = setTimeout(() => {
      slowPollStartedAt.current = startedAt;
      reportRemoteSelectionFailure('slow-poll', undefined, {
        pendingMs: Date.now() - startedAt,
      });
    }, SLOW_REMOTE_POLL_MS);

    return () => clearTimeout(timer);
  }, [isFetching]);
};

/** No-op unless the ship was requested remotely. */
export const reportRemoteZoomFailure = (
  ship: ZoomShip | null,
  reason: RemoteZoomFailureReason,
  error?: unknown,
) => {
  if (!isRemote(ship)) {
    return;
  }
  captureFailure(reason, ship, error);
};

const recordFindTargetSpan = (ship: ZoomShip, result: FindTargetResult) => {
  Sentry.startSpan(
    {
      name: 'remote_zoom.find_target',
      op: 'ui.remote_zoom',
      attributes: {
        'remote_zoom.found': result.found,
        'remote_zoom.mmsi': ship.mmsi ?? '',
        'remote_zoom.imo': String(ship.imo ?? ''),
        ...(result.found
          ? { 'remote_zoom.lat': result.lat, 'remote_zoom.lon': result.lon }
          : { 'remote_zoom.reason': result.reason }),
      },
    },
    () => undefined,
  );
};

interface FindTargetOutcome {
  data: FindTargetResult | null | undefined;
  error: unknown;
}

/**
 * Reports the findTarget outcome for a remote zoom exactly once per zoom
 * request (i.e. per `zoomToShip` object).
 */
export const useReportRemoteFindTargetOutcome = (
  zoomToShip: AutoCompleteShipInfo | null,
  { data, error }: FindTargetOutcome,
) => {
  const reportedFor = useRef<AutoCompleteShipInfo | null>(null);

  useEffect(() => {
    if (!isRemote(zoomToShip) || reportedFor.current === zoomToShip) {
      return;
    }

    if (error) {
      reportedFor.current = zoomToShip;
      reportRemoteZoomFailure(zoomToShip, 'find-target-error', error);
      return;
    }

    if (!data) {
      return;
    }

    reportedFor.current = zoomToShip;
    recordFindTargetSpan(zoomToShip, data);
    addRemoteZoomBreadcrumb('findTarget result', {
      mmsi: zoomToShip.mmsi,
      found: data.found,
    });
    if (!data.found) {
      reportRemoteZoomFailure(zoomToShip, data.reason);
    }
  }, [zoomToShip, data, error]);
};
